import { Mutation, Action } from 'vuex-module-decorators';
import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import { Resolve } from 'vue-di';
import moment, { Moment } from 'moment';
import { AxiosInstance, AxiosRequestConfig } from 'axios';
import { EdgeApi } from '@trialcard/apigateway.client';
import { ContactInfoType } from '@trialcard/enums';
import SessionModule from './session';
import EnrollmentModule from './enrollment';
import SectionModule from './section';
import { Settings } from '~/services/settings';
import { formatAddress } from '~/services/utils';
interface IAddress {
    addressOne: string;
    addressTwo?: string;
    city: string;
    state: string;
    zip: string;
}

interface ICaregiver {
    firstName: string;
    lastName: string;
    dateOfBirth?: Moment | null | string;
    relationshipToPatient: string;
    phoneNumber: string;
    email: string;
}

interface IPrescriber {
    firstName: string;
    middleName?: string;
    lastName: string;
    siteName: string;
    officeContactName: string;
    prescriberNpi: string;
    prescriberTaxId: string;
    prescriberPtan: string;
    addressLineOne: string;
    addressLineTwo?: string;
    prescriberZipCode?: string;
    prescriberCity?: string;
    prescriberState?: string;
    officeContactPhone?: string;
    officeContactFax?: string;
}

interface IPrescriberAPI {
    deaNumber: string;
    firstName: string;
    lastName: string;
    middleName: string;
    npi: string;
    ptan: string;
    stateLicenseId: string;
    suffix: string;
    taxId: string;
    type: string;
}

interface IPharmacyPayorApi {
    bin: string;
    pcn: string;
    insuranceState: string;
    pbmName: string;
    rxMemberId: string;
    payorId: string;
    payorName: string;
    phoneNumber: string;
    faxNumber: string;
    groupNumber: string;
    policyNumber: string;
    planEffectiveDate: string;
    planExpirationDate: string;
    planTerminationDate: string;
    policyName: string;
    policyHolderFirstName: string;
    policyHolderLastName: string;
    insurancePriority: string;
    relationshipToPatient: string;
    address?: IAddress | {};
}

interface ISiteApi {
    id: string | number;
    name: string;
    contactName?: string;
    phoneNumber?: string;
    faxNumber?: string;
    description?: string;
    type?: string;
    address?: IAddress | {};
}

export interface IPrescription {
    id?: string;
    daySupply?: string;
    directions?: string;
    frequency?: number;
    ndc?: string;
    quantity?: number;
    refills?: number;
    writtenOn?: string;
}

interface PatientInformation {
    IsPatientAgeBetween6And17: string;
    firstName: string;
    lastName: string;
    dateOfBirth: Moment | null | string;
    gender: string;
    addressOne: string;
    addressTwo: string;
    zip: string;
    city: string;
    state: string;
    patientPhone: string;
    patientHomePhone: string;
    email: string;
    caregiverFirstName: string;
    relationShipToPatient: string;
    caregiverPhone: string;
    caregiverHomePhone: string;
    caregiverEmail: string;
    prescriberSignature: string;
    todaysDate: string;
}

interface HealthInsuranceInformation {
    hasMedicalInsurance: boolean | null;
    hasPharmacyInsurance: boolean | null;
    hasNoInsurance: boolean | null;
    medicalFirstName: string;
    medicalLastName: string;
    medicalInsuranceName: string;
    medicalInsuranceId: string;
    medicalGroupNumber: string;
    rxInsurance: string;
    rxGroup: string;
    rxId: string;
    rxBin: string;
    rxPcn: string;
}

interface DiagnosisInformation {
    selectedDiagnoses: string[];
    infusionLocation: string | null;
    infusionSiteName: string;
    hospitalAffiliation: string;
    siteStreetAddress: string;
    siteSuite: string;
    siteCity: string;
    siteState: string;
    siteZipCode: string;
    siteContactName: string;
    siteContactPhone: string;
    siteContactEmailAddress: string;
    siteContactFax: string;
}
export interface PharmacyPrescriptionInformation {
    preferredPharmacy?: string;
    patientWeight?: string;
    dateOfRecord?: Moment | null | string;
    prescriptionFor?: string;
    prescriberSignature?: string;
    allPrescriptions?: IPrescription[];
    todaysDate?: string;
    writtenOn?: string;
}

interface GettingStarted {
    newPatient: null | boolean;
    restartingPatient: null | boolean;
    switchingPatient: null | boolean;
    allSupportServices: null | boolean;
    benefitsInvestigation: null | boolean;
    copayServices: null | boolean;
    injectionSupport: null | boolean;
    interpreterNeeded: null | boolean;
    hearingImpaired: null | boolean;
    language: string | null;
    otherBrandName: string | null;
}

enum SupportTypes {
    EyeDropSupportiveCare = 'Eye Drop Supportive Care',
    BenefitsInvestigation = 'BI',
    Copay = 'Co-pay',
    InjectionSupport = 'Injection Training',
}

export interface IAnswer {
    key: string;
    value: string | boolean;
}

export interface IQuestion {
    key: string;
    answers: IAnswer[];
}

export interface ISurvey {
    key: string;
    questions: IQuestion[];
}

interface ISurveyData {
    surveys: ISurvey[];
}

interface ISocSiteApi {
    contactName: string;
    description: string;
    faxNumber: string;
    id: string;
    name: string;
    phoneNumber: string;
    type: string;
    address?: IAddress;
}

interface IMedicalPayorApi {
    payorId: string;
    payorName: string;
    phoneNumber: string;
    faxNumber: string;
    groupNumber: string;
    policyNumber: string;
    planEffectiveDate: string;
    planExpirationDate: string;
    planTerminationDate: string;
    policyName: string;
    policyHolderFirstName: string;
    policyHolderLastName: string;
    insurancePriority: string;
    relationshipToPatient: string;
    address?: IAddress | {};
}

interface IClinicalInformation {
    diagnosisCode: string;
    otherDiagnosisCode: string;
    blenrepIV: boolean;
    quantity: string;
    patientWeight: string;
    regimen: string;
    otherRegimen: string;
}

interface FoundationInformation {
    enrollInFoundation: boolean | null;
    mbi: string;
    enrollInPap: boolean;
    householdIncome: string;
    familyMembers: string;
}

export const NDC_MAP: Record<string, string> = {
    '45 mg': '61314065101',
    '90 mg': '61314065201',
    '260 mg dose': '61314065494',
    '390 mg dose': '61314065494',
    '520 mg dose': '61314065494',
};

@InjectModule({ stateFactory: true }, module)
export default class PatientSupportServices extends InjectVuexModule {
    @Resolve
    public session!: SessionModule;

    @Resolve
    public settings!: Settings;

    @Resolve
    public edgeApi!: EdgeApi;

    @Resolve
    public enrollment!: EnrollmentModule;

    @Resolve
    public section!: SectionModule;

    public isPatientAdult: boolean | null = null;
    public gettingStarted: GettingStarted = {
        newPatient: null,
        restartingPatient: null,
        switchingPatient: null,
        allSupportServices: null,
        benefitsInvestigation: null,
        copayServices: null,
        injectionSupport: null,
        interpreterNeeded: null,
        hearingImpaired: null,
        language: '',
        otherBrandName: '',
    };

    public healthInsuranceInformation: HealthInsuranceInformation = {
        hasMedicalInsurance: null as boolean | null,
        hasPharmacyInsurance: null as boolean | null,
        hasNoInsurance: null as boolean | null,
        medicalFirstName: '',
        medicalLastName: '',
        medicalInsuranceName: '',
        medicalInsuranceId: '',
        medicalGroupNumber: '',
        rxInsurance: '',
        rxGroup: '',
        rxId: '',
        rxBin: '',
        rxPcn: '',
    };

    public diagnosisInformation = {
        selectedDiagnoses: [] as string[],
        infusionLocation: null as string | null,
        infusionSiteName: '',
        hospitalAffiliation: '',
        siteStreetAddress: '',
        siteSuite: '',
        siteCity: '',
        siteState: '',
        siteZipCode: '',
        siteContactName: '',
        siteContactPhone: '',
        siteContactEmailAddress: '',
        siteContactFax: '',
    };

    public prescriberInformation: IPrescriber = {
        firstName: '',
        lastName: '',
        siteName: '',
        officeContactName: '',
        prescriberNpi: '',
        prescriberTaxId: '',
        prescriberPtan: '',
        addressLineOne: '',
        addressLineTwo: '',
        prescriberZipCode: '',
        prescriberCity: '',
        prescriberState: '',
        officeContactPhone: '',
        officeContactFax: '',
    };

    public pharmacyPrescriptionInformation: PharmacyPrescriptionInformation = {
        preferredPharmacy: '',
        patientWeight: '',
        dateOfRecord: null as Moment | string | null,
        prescriptionFor: '',
        prescriberSignature: '',
        allPrescriptions: [] as IPrescription[],
        todaysDate: moment.utc().format('MM/DD/YYYY'),
    };

    public surveys: ISurvey[] = [];

    public clinicalInformation: IClinicalInformation = {
        diagnosisCode: '',
        otherDiagnosisCode: '',
        blenrepIV: false,
        quantity: '',
        patientWeight: '',
        regimen: '',
        otherRegimen: '',
    };

    public foundationInformation: FoundationInformation = {
        enrollInFoundation: null,
        mbi: '',
        enrollInPap: false,
        householdIncome: '',
        familyMembers: '',
    };

    @Mutation
    public updateGettingStartedData(payload: GettingStarted) {
        this.gettingStarted = payload;
    }

    @Mutation
    public updateHealthInsuranceInformation(payload: HealthInsuranceInformation) {
        this.healthInsuranceInformation = payload;
    }

    @Mutation
    public updateDiagnosisInformation(payload: DiagnosisInformation | any) {
        this.diagnosisInformation = payload;
    }

    @Mutation
    public updatePrescriberInformation(payload: IPrescriber) {
        this.prescriberInformation = payload;
    }

    @Mutation
    public updatePharmacyPrescriptionInformation(payload: PharmacyPrescriptionInformation) {
        this.pharmacyPrescriptionInformation = payload;
    }

    @Mutation
    public updateIsPatientAdult(payload: boolean | null) {
        this.isPatientAdult = payload;
    }

    @Mutation
    public addSurvey(payload: { surveyKey: string; questions: IQuestion[] }): void {
        const { surveyKey, questions } = payload;
        if (!payload || !questions) return;
        const existingSurveyIndex = this.surveys.findIndex(s => s.key === surveyKey);
        if (existingSurveyIndex !== -1) {
            this.surveys[existingSurveyIndex] = { key: surveyKey, questions };
        } else {
            this.surveys.push({ key: surveyKey, questions });
        }
    }

    @Mutation
    public updateClinicalInformation(payload: IClinicalInformation) {
        this.clinicalInformation = payload;
    }

    @Mutation
    public updateFoundationInformation(payload: FoundationInformation) {
        this.foundationInformation = payload;
    }

    @Mutation
    public resetStore() {
        this.surveys = [];
        this.clinicalInformation = {
            diagnosisCode: '',
            otherDiagnosisCode: '',
            blenrepIV: false,
            quantity: '',
            patientWeight: '',
            regimen: '',
            otherRegimen: '',
        };
        this.foundationInformation = {
            enrollInFoundation: null,
            mbi: '',
            enrollInPap: false,
            householdIncome: '',
            familyMembers: '',
        };
    }

    private formatPhone(phone: string): string {
        return phone.replace(/\D/g, '');
    }

    private mapAddress(address: IAddress): IAddress {
        return {
            addressOne: address.addressOne,
            addressTwo: address.addressTwo,
            city: address.city,
            state: address.state,
            zip: address.zip,
        };
    }

    private mapCaregiverForPatient(): ICaregiver | null {
        const patientOtherInformation = this.enrollment.morePatientInfoData;
        return patientOtherInformation.alternateContactName || patientOtherInformation.alternateContactPhone
            ? {
                  firstName: patientOtherInformation.alternateContactName || '',
                  lastName: '',
                  dateOfBirth: '',
                  relationshipToPatient: 'Other',
                  phoneNumber: patientOtherInformation.alternateContactPhone
                      ? this.formatPhone(patientOtherInformation.alternateContactPhone)
                      : '',
                  email: '',
              }
            : null;
    }

    private mapCaregiverForHCP(): ICaregiver | null {
        const patientOtherInformation = this.enrollment.hcpPatientInformationData;
        return patientOtherInformation.hcpAlternateRelationship || patientOtherInformation.hcpAlternatePhone
            ? {
                  firstName: patientOtherInformation.hcpAlternateName || '',
                  lastName: '',
                  dateOfBirth: '',
                  relationshipToPatient: 'Other',
                  phoneNumber: patientOtherInformation.hcpAlternatePhone ? this.formatPhone(patientOtherInformation.hcpAlternatePhone) : '',
                  email: '',
              }
            : null;
    }

    private mapPrescribers(): IPrescriberAPI {
        const prescriberData = this.enrollment.prescriberData[0].model;
        return {
            deaNumber: '',
            firstName: prescriberData && prescriberData.firstName ? prescriberData.firstName : '',
            lastName: prescriberData && prescriberData.lastName ? prescriberData.lastName : '',
            middleName: '',
            npi: prescriberData && prescriberData.npi ? prescriberData.npi : '',
            ptan: '',
            stateLicenseId: '',
            suffix: '',
            taxId: '',
            type: 'Primary Care',
        };
    }

    private mapPrescribersHcp(): IPrescriberAPI {
        const prescriberData = this.enrollment.prescriberSiteInformationData;
        return {
            deaNumber: '',
            firstName: prescriberData && prescriberData.firstName ? prescriberData.firstName : '',
            lastName: prescriberData && prescriberData.prescriberSiteLastName ? prescriberData.prescriberSiteLastName : '',
            middleName: '',
            npi: prescriberData && prescriberData.npi ? prescriberData.npi : '',
            ptan: '',
            stateLicenseId: prescriberData && prescriberData.stateLicense ? prescriberData.stateLicense : '',
            suffix: '',
            taxId: prescriberData && prescriberData.taxId ? prescriberData.taxId : '',
            type: 'Primary Care',
        };
    }

    private mapMedicalPayorsForPatient(): IMedicalPayorApi[] {
        const insuranceInfo = this.enrollment.insuranceInformationData;
        const medicalPayors = [];

        // Only add primary if sharedInformation is true
        if (this.enrollment.optSharedInformation === true) {
            const primaryMedicalPayor = {
                payorId: '',
                payorName: insuranceInfo.insuranceProvider || '',
                phoneNumber: insuranceInfo.phone ? this.formatPhone(insuranceInfo.phone) : '',
                faxNumber: '',
                groupNumber: insuranceInfo.group || '',
                policyNumber: insuranceInfo.policy || '',
                planEffectiveDate: '',

                planExpirationDate: '',
                planTerminationDate: '',
                policyName: insuranceInfo.insuranceProvider || '',
                policyHolderFirstName: insuranceInfo.cardHolderName || '',
                policyHolderLastName: '',
                insurancePriority: 'Primary',
                relationshipToPatient: '',
                address: {},
            };
            medicalPayors.push(primaryMedicalPayor);
        }

        // Only add secondary if insurance provider field has a valid value
        if (insuranceInfo.optInsuranceProvider && insuranceInfo.optInsuranceProvider.trim() !== '') {
            const secondaryMedicalPayor = {
                payorId: '',
                payorName: insuranceInfo.optInsuranceProvider || '',
                phoneNumber: insuranceInfo.optPhone ? this.formatPhone(insuranceInfo.optPhone) : '',
                faxNumber: '',
                groupNumber: insuranceInfo.optGroup || '',
                policyNumber: insuranceInfo.optPolicy || '',
                planEffectiveDate: '',

                planExpirationDate: '',
                planTerminationDate: '',
                policyName: insuranceInfo.optInsuranceProvider || '',
                policyHolderFirstName: insuranceInfo.optCardHolderName || '',
                policyHolderLastName: '',
                insurancePriority: 'Secondary',
                relationshipToPatient: '',
                address: {},
            };
            medicalPayors.push(secondaryMedicalPayor);
        }

        return medicalPayors;
    }

    private mapMedicalPayorsHcp(): IMedicalPayorApi[] {
        const insuranceInfo = this.enrollment.hcpInsuranceInformationData;
        const selectInsuranceOption = this.enrollment.selectInsuranceOption;
        const medicalPayors = [];

        const primaryMedicalPayor = {
            payorId: '',
            payorName: insuranceInfo.hcpInsuranceProvider || '',
            phoneNumber: insuranceInfo.hcpPhone ? this.formatPhone(insuranceInfo.hcpPhone) : '',
            faxNumber: '',
            groupNumber: insuranceInfo.hcpGroup || '',
            policyNumber: insuranceInfo.hcpPolicy || '',
            planEffectiveDate: '',

            planExpirationDate: '',
            planTerminationDate: '',
            policyName: insuranceInfo.hcpInsuranceProvider || '',
            policyHolderFirstName: insuranceInfo.hcpCardHolderName || '',
            policyHolderLastName: '',
            insurancePriority: 'Primary',
            relationshipToPatient: '',
            address: {},
        };

        const secondaryMedicalPayor = {
            payorId: '',
            payorName: insuranceInfo.hcpOptInsuranceProvider || '',
            phoneNumber: insuranceInfo.hcpOptPhone ? this.formatPhone(insuranceInfo.hcpOptPhone) : '',
            faxNumber: '',
            groupNumber: insuranceInfo.hcpOptGroup || '',
            policyNumber: insuranceInfo.hcpOptPolicy || '',
            planEffectiveDate: '',

            planExpirationDate: '',
            planTerminationDate: '',
            policyName: insuranceInfo.hcpOptInsuranceProvider || '',
            policyHolderFirstName: insuranceInfo.hcpOptCardHolderName || '',
            policyHolderLastName: '',
            insurancePriority: 'Secondary',
            relationshipToPatient: '',
            address: {},
        };

        if (selectInsuranceOption === 'primary' || selectInsuranceOption === 'primarySecondary') {
            medicalPayors.push(primaryMedicalPayor);
        }

        // Only add secondary if insurance provider field has a valid value
        if (selectInsuranceOption === 'primarySecondary' &&
            insuranceInfo.hcpOptInsuranceProvider &&
            insuranceInfo.hcpOptInsuranceProvider.trim() !== '') {
            medicalPayors.push(secondaryMedicalPayor);
        }

        return medicalPayors;
    }

    private mapPharmacyPayors(): IPharmacyPayorApi {
        return {
            payorId: '',
            payorName: this.healthInsuranceInformation.rxInsurance,
            phoneNumber: '',
            faxNumber: '',
            groupNumber: this.healthInsuranceInformation.rxGroup,
            policyNumber: this.healthInsuranceInformation.rxId,
            bin: this.healthInsuranceInformation.rxBin,
            pcn: this.healthInsuranceInformation.rxPcn,
            insuranceState: '',
            pbmName: '',
            rxMemberId: '',
            planEffectiveDate: '',
            planExpirationDate: '',
            planTerminationDate: '',
            policyHolderFirstName: '',
            policyHolderLastName: '',
            insurancePriority: 'Primary',
            relationshipToPatient: '',
            policyName: this.healthInsuranceInformation.rxInsurance,
            address: {},
        };
    }

    private mapSites(): ISiteApi[] {
        const siteData = this.enrollment.siteData[0].model;
        const siteOptedIn = this.enrollment.optedInOncologistInformation;
        const patientSupportServices = this.enrollment.patientSupportServicesData;

        const addressObj: IAddress = {
            addressOne: (siteData && siteData.addresses && siteData.addresses[0] && siteData.addresses[0].addressOne) || 'UNKNOWN',
            addressTwo: (siteData && siteData.addresses && siteData.addresses[0] && siteData.addresses[0].addressTwo) || '',
            city: (siteData && siteData.addresses && siteData.addresses[0] && siteData.addresses[0].city) || 'UNKNOWN',
            state: (siteData && siteData.addresses && siteData.addresses[0] && siteData.addresses[0].state) || 'UNKNOWN',
            zip: (siteData && siteData.addresses && siteData.addresses[0] && siteData.addresses[0].zip) || '00000',
        };
        const phoneNumbers = siteData && siteData.phoneNumbers && siteData.phoneNumbers.length > 0 ? siteData.phoneNumbers : [];
        const faxPhoneNumber = phoneNumbers.find(phone => phone.phoneType === ContactInfoType.FAX);
        const phoneNumber = phoneNumbers.find(phone => phone.phoneType === ContactInfoType.PHONE);

        const siteInfo = [];

        if(siteOptedIn){
            siteInfo.push({
                contactName: patientSupportServices.ecpName || '',
                description: siteData?.description || '',
                faxNumber: faxPhoneNumber && faxPhoneNumber.phoneNumber ? this.formatPhone(faxPhoneNumber.phoneNumber) : '',
                id: '',
                name: siteData?.description || 'UNKNOWN',
                phoneNumber: phoneNumber && phoneNumber.phoneNumber ? this.formatPhone(phoneNumber.phoneNumber) : '',
                type: '',
                address: formatAddress(addressObj),
            })
        }

        return siteInfo;
    }
    private mapSitesHcp(): ISiteApi[] {
        const siteInfo = this.enrollment.prescriberSiteInformationData;

        const addressObj: IAddress = {
            addressOne: siteInfo.street || 'UNKNOWN',
            addressTwo: '',
            city: siteInfo.city || 'UNKNOWN',
            state: siteInfo.state || 'UNKNOWN',
            zip: siteInfo.zip || '00000',
        };

        const phoneNumber = siteInfo.phone;

        const siteInfoHcp = [];

        if(siteInfo.practiceName || siteInfo.siteFax || phoneNumber || addressObj.addressOne || addressObj.addressTwo || addressObj.city || addressObj.state || addressObj.zip){
            siteInfoHcp.push({
                contactName: siteInfo.contactName || '',
                description: siteInfo.practiceName || '',
                faxNumber: siteInfo.siteFax ? this.formatPhone(siteInfo.siteFax) : '',
                id: '',
                name: siteInfo.practiceName || 'UNKNOWN',
                phoneNumber: phoneNumber ? this.formatPhone(phoneNumber) : '',
                type: '',
                address: formatAddress(addressObj),
            })
        }


        return siteInfoHcp;
    }

    private mapSocSitesForPatient(): ISocSiteApi[] {
        const infusionSiteInfo = this.enrollment.patientSupportServicesData;
        const socSite = [];
        if(infusionSiteInfo.eyeCareQuestionOne){
            socSite.push({
                    contactName: infusionSiteInfo.ecpName || '',
                    description: infusionSiteInfo.facilityName || '',
                    faxNumber: infusionSiteInfo.patientSupportFax ? this.formatPhone(infusionSiteInfo.patientSupportFax) : '',
                    id: '',
                    name: infusionSiteInfo.facilityName || 'UNKNOWN',
                    phoneNumber: infusionSiteInfo.patientSupportPhone ? this.formatPhone(infusionSiteInfo.patientSupportPhone) : '',
                    type: '',
                    address: {
                        addressOne: infusionSiteInfo.mailingAddress || 'UNKNOWN',
                        addressTwo: '',
                        city: infusionSiteInfo.city || 'UNKNOWN',
                        state: infusionSiteInfo.state || 'UNKNOWN',
                        zip: infusionSiteInfo.zip || '00000',
                    },
            });
        }
        return socSite;
    }

    private mapSocSitesForHCP(): ISocSiteApi[] {
        const infusionSiteInfo = this.enrollment.prescriberSiteInformationData;
        const infusionOptedIn = this.enrollment.selectPrescriberSiteOption;
        const eyeCareInfusionSiteInfo = this.enrollment.hcpSupportServicesData;
        const prescriberSiteInfo = this.enrollment.prescriberSiteInformationData;
        const socSite = []
        if(infusionOptedIn){
            socSite.push({
                contactName:  prescriberSiteInfo.contactName || '',
                description: infusionSiteInfo.facultyName || '',
                faxNumber: '',
                id: '',
                name: infusionSiteInfo.facultyName || 'UNKNOWN',
                phoneNumber: infusionSiteInfo.optPhone ? this.formatPhone(infusionSiteInfo.optPhone) : '',
                type: '',
                address: {
                    addressOne: infusionSiteInfo.optStreet || 'UNKNOWN',
                    addressTwo: '',
                    city: infusionSiteInfo.optCity || 'UNKNOWN',
                    state: infusionSiteInfo.optState || 'UNKNOWN',
                    zip: infusionSiteInfo.optZip || '00000',
                },
            })
        }
        if(eyeCareInfusionSiteInfo.eyeCareQuestionOne){
            socSite.push({
                contactName: eyeCareInfusionSiteInfo.ecpName || '',
                description: eyeCareInfusionSiteInfo.facilityName || '',
                faxNumber: eyeCareInfusionSiteInfo.patientSupportFax ? this.formatPhone(eyeCareInfusionSiteInfo.patientSupportFax) : '',
                id: '',
                name: eyeCareInfusionSiteInfo.facilityName || 'UNKNOWN',
                phoneNumber: eyeCareInfusionSiteInfo.patientSupportPhone
                    ? this.formatPhone(eyeCareInfusionSiteInfo.patientSupportPhone)
                    : '',
                type: '',
                address: {
                    addressOne: eyeCareInfusionSiteInfo.mailingAddress || 'UNKNOWN',
                    addressTwo: '',
                    city: eyeCareInfusionSiteInfo.city || 'UNKNOWN',
                    state: eyeCareInfusionSiteInfo.state || 'UNKNOWN',
                    zip: eyeCareInfusionSiteInfo.zip || '00000',
                },
            })
        }
        return socSite;
    }

    private getSupportServices() {
        const supportServices: string[] = [];
        // const patientSupportServices = this.enrollment.patientSupportServicesData;
        // if(patientSupportServices.eyeCareDropQuestionTwo){
        //     supportServices.push(SupportTypes.EyeDropSupportiveCare);
        // }
        return supportServices;
    }

    private getSupportServicesHcp() {
        const supportServices: string[] = [];
        // const patientSupportServices = this.enrollment.hcpSupportServicesData;
        // if(patientSupportServices.eyeCareDropQuestionTwo){
        //     supportServices.push(SupportTypes.EyeDropSupportiveCare);
        // }
        return supportServices;
    }

    private mapDiagnosisCodesForHCP() {
        const diagnosisCodes = this.clinicalInformation.diagnosisCode;
        const otherDiagnosisCode = this.clinicalInformation.otherDiagnosisCode;
        return diagnosisCodes !== 'other' && diagnosisCodes.trim() !== ''
            ? [diagnosisCodes]
            : diagnosisCodes === 'other'
            ? [otherDiagnosisCode]
            : [];
    }

    private mapPrescriptionsForHCP() {
        const regimen =
            this.clinicalInformation.regimen !== 'other' && this.clinicalInformation.regimen.trim() !== ''
                ? this.clinicalInformation.regimen
                : this.clinicalInformation.regimen === 'other' && this.clinicalInformation.otherRegimen.trim() !== ''
                ? this.clinicalInformation.otherRegimen
                : '';
        const prescription = {
            daySupply: '',
            directions: regimen,
            frequency: 0,
            ndc: '00173091301',
            quantity: this.clinicalInformation.quantity,
            refills: 0,
            writtenOn: moment.utc().format('MM/DD/YYYY'),
        };
        return [prescription];
    }

    public formatAPIPayloadForPatient(includeConsent = false) {
        // patient information
        const patientInformation = this.enrollment.patientInformationData;
        const phone0 =
            patientInformation.phoneNumbers && patientInformation.phoneNumbers.length > 0 ? patientInformation.phoneNumbers[0] : undefined;
        const email0 =
            patientInformation.emailAddresses && patientInformation.emailAddresses.length > 0
                ? patientInformation.emailAddresses[0]
                : undefined;
        const address0 = patientInformation.addresses && patientInformation.addresses.length > 0 ? patientInformation.addresses[0] : {};

        return {
            callingTransactionID: '',
            callingTransactionType: 'Enrollment',
            caseSource: 'Website Enrollment',
            programCode: 'GSKO',
            productFamily: 'Blenrep',
            diagnosisCodes: [],
            preferredPharmacy: [],
            servicesNeeded: this.getSupportServices(),
            mercalisHubId: '',
            thirdPartyHubId: '',
            thirdPartyCaseId: '',
            patient: {
                firstName: patientInformation.firstName,
                lastName: patientInformation.lastName,
                middleName: '',
                dateOfBirth: patientInformation.dateOfBirth ? moment(patientInformation.dateOfBirth).format('MM/DD/YYYY') : '',
                cellPhone:
                    phone0 && phone0.phoneNumber && phone0.phoneType === ContactInfoType.MOBILE ? this.formatPhone(phone0.phoneNumber) : '',
                emailAddress: email0 && email0.address ? email0.address : '',
                emailOutput: false,
                gender: patientInformation.gender === 1 ? 'Male' : patientInformation.gender === 2 ? 'Female' : patientInformation.gender === 0 ? 'Unknown' : '',
                homePhone:
                    phone0 && phone0.phoneNumber && phone0.phoneType === ContactInfoType.PHONE ? this.formatPhone(phone0.phoneNumber) : '',
                mailOptOut: false,
                phoneOptOut: false,
                preferredCommunicationType: this.enrollment.morePatientInfoData.optIn ? 'SMS' : 'Other',
                preferredLanguage: email0 && email0.language ? email0.language : '',
                race: '',
                residentOfUS: true,
                suffix: 'I',
                textOptOut: false,
                videoOptOut: false,
                voicemailOptOut: false,
                workPhone: '',
                address: this.mapAddress({
                    addressOne: address0.addressOne || 'UNKNOWN',
                    addressTwo: '',
                    city: address0.city || 'UNKNOWN',
                    state: address0.state || 'UNKNOWN',
                    zip: address0.zip || '00000',
                }),
                caregiverContact: this.mapCaregiverForPatient(),
            },
            medicalPayors: this.mapMedicalPayorsForPatient(),
            pharmacyPayors: [],
            socSites: this.mapSocSitesForPatient(),
            sites: this.mapSites(),
            prescribers: [this.mapPrescribers()],
            prescriptions: [],
            consent: includeConsent
            ? [
                {
                    consentEndDate: '',
                    consentParty: 'Patient',
                    consentStartDate: moment.utc().format('MM/DD/YYYY'),
                    consentType: 'HIPAA Consent',
                    responseType: 'Granted',
                },
            ] : [],
            surveys: this.surveys,
            attachments: [],
        };
    }

    public formatAPIPayloadForHCP(includeConsent = false) {
        // patient information
        const patientInformation = this.enrollment.hcpPatientInformationData;

        return {
            callingTransactionID: '',
            callingTransactionType: 'Enrollment',
            caseSource: 'Website Enrollment',
            programCode: 'GSKO',
            productFamily: 'Blenrep',
            diagnosisCodes: this.mapDiagnosisCodesForHCP(),
            preferredPharmacy: [],
            servicesNeeded: this.getSupportServicesHcp(),
            mercalisHubId: '',
            thirdPartyHubId: '',
            thirdPartyCaseId: '',
            patient: {
                firstName: patientInformation.hcpFirstName,
                lastName: patientInformation.hcpLastName,
                middleName: '',
                dateOfBirth: patientInformation.hcpDateOfBirth ? moment(patientInformation.hcpDateOfBirth).format('MM/DD/YYYY') : '',
                cellPhone: patientInformation.hcpPhoneType === '2' ? this.formatPhone(patientInformation.hcpPreferredPhone) : '',
                emailAddress: patientInformation.hcpEmail || '',
                emailOutput: false,
                gender: patientInformation.hcpGender === 1 ? 'Male' : patientInformation.hcpGender === 2 ? 'Female' : patientInformation.hcpGender === 0 ? 'Unknown' : '',
                homePhone: patientInformation.hcpPhoneType === '1' ? this.formatPhone(patientInformation.hcpPreferredPhone) : '',
                mailOptOut: false,
                phoneOptOut: false,
                preferredCommunicationType: 'Other',
                preferredLanguage: patientInformation.hcpLanguage || '',
                race: '',
                residentOfUS: true,
                suffix: 'I',
                textOptOut: false,
                videoOptOut: false,
                voicemailOptOut: false,
                workPhone: '',
                address: this.mapAddress({
                    addressOne: patientInformation.hcpStreet || 'UNKNOWN',
                    addressTwo: '',
                    city: patientInformation.hcpCity || 'UNKNOWN',
                    state: patientInformation.hcpState || 'UNKNOWN',
                    zip: patientInformation.hcpZip || '00000',
                }),
                caregiverContact: this.mapCaregiverForHCP(),
            },
            medicalPayors: this.mapMedicalPayorsHcp(),
            pharmacyPayors: [],
            socSites: this.mapSocSitesForHCP(),
            sites: this.mapSitesHcp(),
            prescribers: [this.mapPrescribersHcp()],
            prescriptions: this.mapPrescriptionsForHCP(),
            consent: includeConsent
            ? [
                {
                    consentEndDate: '',
                    consentParty: 'HCP',
                    consentStartDate: moment.utc().format('MM/DD/YYYY'),
                    consentType: 'HIPAA Consent',
                    responseType: 'Granted',
                },
            ] : [],
            surveys: this.surveys,
            attachments: [],
        };
    }

    @Action({})
    async submitClsEnrollment(payload: any) {
        try {
            const axios: AxiosInstance = (this.edgeApi as any).axios;
            const axiosRequest: AxiosRequestConfig = {
                url: 'edge/cls/ClsEnrollment/enroll',
                method: 'POST',
                data: payload,
                headers: {
                    'x-program-id': this.settings.clsProgramId!,
                },
            };
            await Promise.all([
                (this.edgeApi as any).authentications.Token.applyToRequest(axiosRequest),
            ]);
            const { data } = await axios.request(axiosRequest);
            return data.data && data.success;
        } catch (error){
            console.error('Error submitting CLS enrollment:', error);
            return false;
        }
    }
}
